import React, { useState } from 'react';
import {
  X,
  UserCheck,
  CheckCircle2,
  StickyNote,
  Tag as TagIcon
} from 'lucide-react';
import {
  Agent,
  Conversation,
  ConversationNote,
  ConversationStatus
} from '../../../types/models/whatsapp';

interface ContextPanelProps {
  conversation: Conversation;
  notes: ConversationNote[];
  agents: Agent[];
  onClose: () => void;
  onAssign: (agentId: string | null) => Promise<void>;
  onStatusChange: (status: ConversationStatus) => Promise<void>;
  onAddNote: (body: string) => Promise<void>;
  onSetTags: (tags: string[]) => Promise<void>;
  /** Tags already in use across conversations, offered as autocomplete. */
  suggestedTags?: string[];
}

/** Keeps tags consistent so 'VIP', 'vip' and ' vip ' don't become three tags. */
const normalizeTag = (raw: string): string =>
  raw.trim().toLowerCase().replace(/\s+/g, '-').slice(0, 32);

const formatTimestamp = (timestamp: number): string =>
  new Date(timestamp).toLocaleString([], {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });

const ContextPanel: React.FC<ContextPanelProps> = ({
  conversation,
  notes,
  agents,
  onClose,
  onAssign,
  onStatusChange,
  onAddNote,
  onSetTags,
  suggestedTags = []
}) => {
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [tagDraft, setTagDraft] = useState('');
  const [savingTags, setSavingTags] = useState(false);
  const [tagError, setTagError] = useState<string | null>(null);

  const commitTags = async (tags: string[]) => {
    setSavingTags(true);
    setTagError(null);
    try {
      await onSetTags(tags);
    } catch (error: any) {
      setTagError(error.message || 'Failed to update tags');
    } finally {
      setSavingTags(false);
    }
  };

  const handleAddTag = async () => {
    const tag = normalizeTag(tagDraft);
    if (!tag || savingTags) return;

    if (conversation.tags.includes(tag)) {
      setTagDraft('');
      return;
    }

    setTagDraft('');
    await commitTags([...conversation.tags, tag]);
  };

  const handleRemoveTag = async (tag: string) => {
    if (savingTags) return;
    await commitTags(conversation.tags.filter((t) => t !== tag));
  };

  const handleTagKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddTag();
    } else if (event.key === 'Backspace' && !tagDraft && conversation.tags.length) {
      // Backspace on an empty input removes the last tag, as in most tag inputs.
      event.preventDefault();
      handleRemoveTag(conversation.tags[conversation.tags.length - 1]);
    }
  };

  const handleAddNote = async () => {
    const body = noteDraft.trim();
    if (!body || savingNote) return;

    setSavingNote(true);
    try {
      await onAddNote(body);
      setNoteDraft('');
    } catch (error) {
      console.error('❌ Failed to add note:', error);
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <aside className="flex h-full w-full flex-col border-l border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-200 p-3">
        <h3 className="text-sm font-semibold text-gray-900">
          Contact details
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-gray-400 hover:bg-gray-100"
          aria-label="Close details"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="border-b border-gray-200 p-4 text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-wa-teal text-xl font-semibold text-white">
            {(conversation.contactName || conversation.contactPhone)
              .slice(0, 2)
              .toUpperCase()}
          </span>
          <p className="mt-2 text-sm font-medium text-gray-900">
            {conversation.contactName || 'Unknown contact'}
          </p>
          <p className="text-xs text-gray-500">
            +{conversation.contactPhone}
          </p>
        </div>

        <div className="border-b border-gray-200 p-4">
          <label
            htmlFor="assignee"
            className="flex items-center gap-1.5 text-xs font-medium text-gray-700"
          >
            <UserCheck className="h-3.5 w-3.5" />
            Assigned to
          </label>
          <select
            id="assignee"
            value={conversation.assigneeId || ''}
            onChange={(e) => onAssign(e.target.value || null)}
            className="mt-1.5 w-full rounded-lg border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="">Unassigned</option>
            {agents.map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.name}
              </option>
            ))}
          </select>
        </div>

        <div className="border-b border-gray-200 p-4">
          <span className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Status
          </span>
          <div className="mt-1.5 flex gap-1">
            {(['open', 'pending', 'resolved'] as ConversationStatus[]).map(
              (status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => onStatusChange(status)}
                  className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium capitalize transition-colors ${
                    conversation.status === status
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {status}
                </button>
              )
            )}
          </div>
        </div>

        <div className="border-b border-gray-200 p-4">
          <span className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
            <TagIcon className="h-3.5 w-3.5" />
            Tags
          </span>

          <div className="mt-1.5 flex flex-wrap gap-1">
            {conversation.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 rounded-full bg-primary-50 py-0.5 pl-2 pr-1 text-xs text-primary-700"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => handleRemoveTag(tag)}
                  disabled={savingTags}
                  className="rounded-full p-0.5 hover:bg-primary-100 disabled:opacity-50"
                  aria-label={`Remove tag ${tag}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            {conversation.tags.length === 0 && (
              <span className="text-xs text-gray-400">No tags yet</span>
            )}
          </div>

          <div className="mt-2 flex gap-1">
            <input
              type="text"
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={handleTagKeyDown}
              list="whatsapp-tag-suggestions"
              placeholder="Add a tag…"
              disabled={savingTags}
              className="min-w-0 flex-1 rounded-lg border-gray-300 text-xs focus:border-primary-500 focus:ring-primary-500 disabled:opacity-50"
            />
            <datalist id="whatsapp-tag-suggestions">
              {suggestedTags.map((tag) => (
                <option key={tag} value={tag} />
              ))}
            </datalist>
            <button
              type="button"
              onClick={handleAddTag}
              disabled={!tagDraft.trim() || savingTags}
              className="shrink-0 rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Add
            </button>
          </div>

          {tagError && (
            <p className="mt-1 text-[11px] text-danger-600">{tagError}</p>
          )}
        </div>

        <div className="p-4">
          <span className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
            <StickyNote className="h-3.5 w-3.5" />
            Internal notes
          </span>

          <textarea
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            rows={2}
            placeholder="Add a note (not sent to the contact)"
            className="mt-1.5 w-full resize-none rounded-lg border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
          />
          <button
            type="button"
            onClick={handleAddNote}
            disabled={!noteDraft.trim() || savingNote}
            className="mt-1.5 w-full rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {savingNote ? 'Saving…' : 'Add note'}
          </button>

          <div className="mt-3 space-y-2">
            {notes.map((note) => (
              <div
                key={note.id}
                className="rounded-lg bg-warning-50 p-2"
              >
                <p className="text-xs text-gray-700">
                  {note.body}
                </p>
                <p className="mt-1 text-[10px] text-gray-500">
                  {note.authorName} · {formatTimestamp(note.createdAt)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </aside>
  );
};

export default ContextPanel;
